import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Chip,
  Stack,
  Button,
  IconButton,
  Tooltip,
  Collapse
} from '@mui/material';
import {
  ContentCopy,
  ExpandMore,
  ExpandLess,
  FormatQuote
} from '@mui/icons-material';

interface PromptOption {
  title: string;
  content: string;
  explanation: string;
}

interface PromptDisplayProps {
  content?: string | null;
  onCopy?: (text: string) => void;
}

const PromptDisplay: React.FC<PromptDisplayProps> = ({ content, onCopy }) => {
  const [expandedOptions, setExpandedOptions] = React.useState<number[]>([]);

  // Handle undefined or null content
  if (!content) {
    return (
      <Typography variant="body2" color="text.secondary">
        No content available
      </Typography>
    );
  }

  const parsePromptContent = (text: string): {
    intro?: string;
    options: PromptOption[];
    improvements?: string;
  } => {
    const result: { intro?: string; options: PromptOption[]; improvements?: string } = {
      options: []
    };

    // Check if this is a multi-option prompt
    const optionMatches = text ? text.match(/\*\*Option \d+.*?\*\*[\s\S]*?(?=\*\*Option \d+|\*\*Key improvements|$)/g) : null;
    
    if (optionMatches && optionMatches.length > 0) {
      // Extract intro text before first option
      const firstOptionIndex = text.indexOf('**Option 1');
      if (firstOptionIndex > 0) {
        result.intro = text.substring(0, firstOptionIndex).trim();
      }

      // Parse each option
      optionMatches.forEach((optionText) => {
        const titleMatch = optionText.match(/\*\*Option \d+.*?\*\*/);
        const title = titleMatch ? titleMatch[0].replace(/\*\*/g, '') : '';
        
        const quoteMatch = optionText.match(/> ([\s\S]*?)(?=\*\*Why this works|$)/);
        const content = quoteMatch ? quoteMatch[1].trim() : '';
        
        const explanationMatch = optionText.match(/\*\*Why this works:\*\*[\s\S]*$/);
        const explanation = explanationMatch ? explanationMatch[0].replace('**Why this works:**', '').trim() : '';

        if (title && content) {
          result.options.push({ title, content, explanation });
        }
      });

      // Extract improvements section
      const improvementsMatch = text.match(/\*\*Key improvements[\s\S]*$/);
      if (improvementsMatch) {
        result.improvements = improvementsMatch[0];
      }
    }

    return result;
  };

  const toggleOption = (index: number) => {
    setExpandedOptions(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const parsed = parsePromptContent(content);
  const hasOptions = parsed.options.length > 0;

  // If no options found, display as plain text with basic markdown formatting
  if (!hasOptions) {
    const formattedContent = (content || '')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br />');

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          {onCopy && content && (
            <Tooltip title="Copy prompt">
              <IconButton size="small" onClick={() => onCopy(content)}>
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Typography 
          variant="body1" 
          sx={{ whiteSpace: 'pre-wrap' }}
          dangerouslySetInnerHTML={{ __html: formattedContent }}
        />
      </Box>
    );
  }

  // Display structured options
  return (
    <Box>
      {parsed.intro && (
        <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
          {parsed.intro}
        </Typography>
      )}

      <Stack spacing={2}>
        {parsed.options.map((option, index) => (
          <Paper key={index} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {option.title}
              </Typography>
              <Stack direction="row" spacing={1}>
                {onCopy && (
                  <Tooltip title="Copy this option">
                    <IconButton size="small" onClick={() => onCopy(option.content)}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <IconButton 
                  size="small" 
                  onClick={() => toggleOption(index)}
                >
                  {expandedOptions.includes(index) ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Stack>
            </Stack>

            <Paper sx={{ p: 2, backgroundColor: 'action.hover', mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <FormatQuote sx={{ color: 'text.secondary', mt: 0.5 }} />
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                  {option.content}
                </Typography>
              </Stack>
            </Paper>

            <Collapse in={expandedOptions.includes(index)}>
              {option.explanation && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Why this works:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {option.explanation}
                  </Typography>
                </Box>
              )}
            </Collapse>
          </Paper>
        ))}
      </Stack>

      {parsed.improvements && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography 
            variant="body2" 
            sx={{ whiteSpace: 'pre-wrap' }}
            dangerouslySetInnerHTML={{ 
              __html: parsed.improvements
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/\*(.*?)\*/g, '<em>$1</em>')
                .replace(/\n/g, '<br />')
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default PromptDisplay;